import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { PostStatus } from '@mys/contracts';
import { ApiError } from '../lib/api.js';
import { POST_STATUSES, POST_STATUS_LABELS, usePosts } from '../lib/posts.js';
import { formatDate, formatDateTime } from '../lib/format.js';

/* ============================================================================
   /blog — every post, including the ones the public cannot see.
   ----------------------------------------------------------------------------
   Drafts sort to the top: an unfinished post is the one someone came here to
   work on. That ordering is the API's, not this screen's.
   ========================================================================== */

export function BlogPage() {
  const [params, setParams] = useSearchParams();
  const statusParam = params.get('status');
  const status = POST_STATUSES.includes(statusParam as PostStatus)
    ? (statusParam as PostStatus)
    : undefined;

  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);

  const { data, isPending, error } = usePosts({
    ...(status ? { status } : {}),
    ...(q ? { q } : {}),
  });

  function setParam(key: string, value: string | undefined) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const items = data?.items ?? [];
  const counts = data?.counts ?? {};

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Blog</h1>
          <p className="muted">{data ? `${data.total} post` : 'Loading…'}</p>
        </div>

        <div className="row">
          <form
            className="row"
            onSubmit={(event) => {
              event.preventDefault();
              setParam('q', search.trim() || undefined);
            }}
          >
            <div className="field" style={{ minWidth: 180 }}>
              <label htmlFor="q" className="sr-only">
                Search by title or slug
              </label>
              <input
                id="q"
                type="search"
                placeholder="Title or slug"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <button className="btn btn--ghost btn--small" type="submit">
              Dhoondhiye
            </button>
          </form>
          <Link to="/blog/new" className="btn">
            New post
          </Link>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={status ? 'btn btn--ghost btn--small' : 'btn btn--small'}
          onClick={() => setParam('status', undefined)}
        >
          All
        </button>
        {POST_STATUSES.map((value) => (
          <button
            key={value}
            type="button"
            className={status === value ? 'btn btn--small' : 'btn btn--ghost btn--small'}
            onClick={() => setParam('status', value)}
          >
            {POST_STATUS_LABELS[value]} {counts[value] ?? 0}
          </button>
        ))}
      </div>

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load posts.'}
        </p>
      ) : null}

      {isPending ? <p className="muted">Loading…</p> : null}

      {data && items.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0 }}>
            {status || q ? 'Nothing matches this filter.' : 'No posts yet.'}
          </p>
        </div>
      ) : null}

      <div className="stack">
        {items.map((post) => (
          <article className="card" key={post.id}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div>
                <Link to={`/blog/${post.id}`} className="lead-card__business">
                  {post.title}
                </Link>
                <div className="lead-card__meta">/blog/{post.slug}</div>
              </div>
              <span className={post.status === 'published' ? 'pill' : 'pill pill--muted'}>
                {POST_STATUS_LABELS[post.status]}
              </span>
            </div>

            <div className="lead-card__meta" style={{ marginTop: 8 }}>
              {post.status === 'scheduled' && post.publishedAt
                ? `Goes live ${formatDateTime(post.publishedAt)}`
                : post.status === 'published' && post.publishedAt
                  ? `Live ${formatDate(post.publishedAt)}`
                  : `Written ${formatDate(post.createdAt)}`}
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
